import React, { useState } from "react";
import { siteConfig, serviceBlocks, capabilities, dealStructure, contactInfo, emailjsConfig, redactedCredits, postProductionAssets, placementPartners, vaultTeaser, supervisorToolkit, artistResources, realityCheck, signNowConfig, liveLedgerData } from "../data/mock";
import { ArrowRight, ArrowUpRight, Shield, Zap, Globe, Music, Send, CheckCircle, Loader2, FileDown, Eye, EyeOff, Package, Lock, FileText, Download, Scale, Percent, FileCheck, FileSignature, CheckSquare, FilePlus, Users, BookOpen, Award, Check, PenTool, Mail, Database, ExternalLink, Newspaper, TrendingUp, Unlock, Building2, Briefcase, Tv, Skull } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import LegalBanner from "../components/LegalBanner";
import emailjs from "@emailjs/browser";

const capabilityIcons = [Shield, Zap, Globe, Music];

/* ─── Animated Section Wrapper ─── */
const Section = ({ children, className = "", id }) => {
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

/* ═══════════════ HERO WITH INSTANT VAULT ACCESS ═══════════════ */
const HeroSection = () => {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({ name: "", company: "", needs: "" });
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleVaultAccess = (e) => {
    e.preventDefault();
    if (accessCode.toUpperCase() === "ITZGREEN") {
      // Store authentication and navigate to vault
      sessionStorage.setItem("vaultAuthenticated", "true");
      navigate("/vault");
    } else {
      setCodeError(true);
      setTimeout(() => setCodeError(false), 2000);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Simulate form submission (in production, this would send to your inbox)
    try {
      // EmailJS or backend integration would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      setRequestSubmitted(true);
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[rgb(17,17,19)] pt-10">
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      {/* Glow orb */}
      <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-[rgba(218,255,1,0.03)] rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20 relative z-10 w-full">
        
        {/* ═══ LIABILITY SHIELD BADGES (Top Priority) ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8"
        >
          <div className="flex items-center gap-1.5 bg-[rgba(74,222,128,0.1)] border border-[rgba(74,222,128,0.4)] rounded-lg px-3 py-1.5">
            <Shield size={14} className="text-[rgb(74,222,128)]" />
            <span className="text-[rgb(74,222,128)] text-[10px] sm:text-xs font-bold tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
              ONE-STOP CLEARED
            </span>
          </div>
          <div className="flex items-center gap-1.5 bg-[rgba(218,255,1,0.08)] border border-[rgba(218,255,1,0.3)] rounded-lg px-3 py-1.5">
            <CheckCircle size={14} className="text-[rgb(218,255,1)]" />
            <span className="text-[rgb(218,255,1)] text-[10px] sm:text-xs font-bold tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
              100% HUMAN
            </span>
          </div>
          <div className="flex items-center gap-1.5 bg-[rgba(161,161,170,0.1)] border border-[rgba(161,161,170,0.3)] rounded-lg px-3 py-1.5">
            <FileCheck size={14} className="text-[rgb(161,161,170)]" />
            <span className="text-[rgb(161,161,170)] text-[10px] sm:text-xs font-bold tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
              FULLY RECONCILED
            </span>
          </div>
        </motion.div>

        {/* ═══ MAIN HEADLINE ═══ */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="text-[rgb(218,255,1)] text-[clamp(2rem,6vw,4rem)] font-bold leading-[0.95] tracking-tight max-w-3xl mb-4"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          100% HUMAN-PRODUCED.<br />
          <span className="text-white">ONE-STOP CLEARED. FULLY RECONCILED.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="text-[rgb(161,161,170)] text-sm sm:text-base max-w-xl mb-8"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Premium production music for film, TV, and advertising. Master & Publishing 100% controlled. Single signature clears both sides.
        </motion.p>

        {/* ═══ VAULT QUICK ACCESS WIDGET (3-Second Entry) ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(218,255,1,0.3)] rounded-2xl p-5 sm:p-6 max-w-md mb-10"
          data-testid="vault-quick-access"
        >
          <div className="flex items-center gap-2 mb-4">
            <Lock size={18} className="text-[rgb(218,255,1)]" />
            <h2 className="text-white text-sm sm:text-base font-bold tracking-wide" style={{ fontFamily: "'Oswald', sans-serif" }}>
              SUPERVISOR VAULT ACCESS
            </h2>
          </div>
          
          <form onSubmit={handleVaultAccess} className="space-y-3">
            <div>
              <Input
                type="password"
                placeholder="Enter Access Code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className={`bg-[rgb(17,17,19)] border-[rgba(255,255,255,0.1)] text-white placeholder:text-[rgb(100,100,110)] h-12 text-sm ${codeError ? 'border-red-500 shake' : 'focus:border-[rgb(218,255,1)]'}`}
                data-testid="vault-code-input"
              />
              {codeError && (
                <p className="text-red-400 text-xs mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>Invalid access code</p>
              )}
            </div>
            
            <Button
              type="submit"
              className="w-full bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-bold tracking-wider hover:bg-[rgb(166,190,21)] h-12"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="vault-enter-btn"
            >
              <Unlock size={16} className="mr-2" />
              ENTER VAULT
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
            <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
              <DialogTrigger asChild>
                <button
                  className="text-[rgb(161,161,170)] text-xs hover:text-[rgb(218,255,1)] transition-colors flex items-center gap-1.5"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  data-testid="request-access-btn"
                >
                  <Building2 size={12} />
                  Network Supervisor? Request Access
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[rgb(26,28,30)] border-[rgba(255,255,255,0.1)] text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[rgb(218,255,1)] text-xl" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    REQUEST VAULT ACCESS
                  </DialogTitle>
                  <DialogDescription className="text-[rgb(161,161,170)] text-sm">
                    Access is verified within 24 hours. Only authorized music supervisors and trailer agencies.
                  </DialogDescription>
                </DialogHeader>
                
                {!requestSubmitted ? (
                  <form onSubmit={handleRequestAccess} className="space-y-4 mt-4">
                    <div>
                      <label className="text-[rgb(161,161,170)] text-xs tracking-wider mb-1.5 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                        FULL NAME & TITLE
                      </label>
                      <Input
                        placeholder="Jane Smith, Music Supervisor"
                        value={requestForm.name}
                        onChange={(e) => setRequestForm({...requestForm, name: e.target.value})}
                        required
                        className="bg-[rgb(17,17,19)] border-[rgba(255,255,255,0.1)] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-[rgb(161,161,170)] text-xs tracking-wider mb-1.5 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                        NETWORK / AGENCY / PRODUCTION COMPANY
                      </label>
                      <Input
                        placeholder="Blumhouse Productions"
                        value={requestForm.company}
                        onChange={(e) => setRequestForm({...requestForm, company: e.target.value})}
                        required
                        className="bg-[rgb(17,17,19)] border-[rgba(255,255,255,0.1)] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-[rgb(161,161,170)] text-xs tracking-wider mb-1.5 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                        ACTIVE PROJECT SLATE & SONIC NEEDS
                      </label>
                      <Textarea
                        placeholder="Need tension beds for Q3 true crime documentary"
                        value={requestForm.needs}
                        onChange={(e) => setRequestForm({...requestForm, needs: e.target.value})}
                        required
                        rows={3}
                        className="bg-[rgb(17,17,19)] border-[rgba(255,255,255,0.1)] text-white resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-bold hover:bg-[rgb(166,190,21)]"
                    >
                      {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                      SUBMIT REQUEST
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-6">
                    <CheckCircle size={40} className="text-[rgb(74,222,128)] mx-auto mb-3" />
                    <p className="text-white font-semibold mb-2">Request Received</p>
                    <p className="text-[rgb(161,161,170)] text-sm">
                      We'll verify your credentials and send your access code within 24 hours.
                    </p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </motion.div>

        {/* ═══ CATALOG QUICK-NAV LANES ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <p className="text-[rgb(100,100,110)] text-[10px] tracking-[0.3em] mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            QUICK ACCESS BY CATALOG
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Premium R&B Lane - Nijay Scott */}
            <Link
              to="/vault?folder=scriptedDrama"
              className="flex-1 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 hover:border-[rgba(218,255,1,0.4)] hover:bg-[rgba(218,255,1,0.03)] transition-all duration-300 group"
              data-testid="quick-nav-rnb"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center">
                  <Tv size={16} className="text-[rgb(218,255,1)]" />
                </div>
                <span className="text-white text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  PREMIUM R&B
                </span>
              </div>
              <p className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                Nijay Scott • Scripted Drama • Emotional Pacing
              </p>
            </Link>
            
            {/* High-Tension Urban Lane - Well Made Choice */}
            <Link
              to="/vault?folder=highTensionUrban"
              className="flex-1 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 hover:border-[rgba(218,255,1,0.4)] hover:bg-[rgba(218,255,1,0.03)] transition-all duration-300 group"
              data-testid="quick-nav-urban"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center">
                  <Briefcase size={16} className="text-[rgb(218,255,1)]" />
                </div>
                <span className="text-white text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  HIGH-TENSION URBAN
                </span>
              </div>
              <p className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                Well Made Choice • Cinematic Hip-Hop • Street Anthems
              </p>
            </Link>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-4 sm:left-6 md:left-8 flex items-center gap-3"
        >
          <div className="w-px h-12 bg-gradient-to-b from-[rgb(218,255,1)] to-transparent" />
          <span
            className="text-[rgb(161,161,170)] text-[10px] tracking-[0.3em] uppercase"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Scroll
          </span>
        </motion.div>
      </div>
    </section>
  );
};

/* ═══════════════ THE 50% REALITY CHECK (NEW) ═══════════════ */
const RealityCheckSection = () => (
  <Section className="bg-[rgb(17,17,19)] py-16 md:py-24 lg:py-32">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8">
      <div className="bg-[rgb(26,28,30)] border border-[rgba(218,255,1,0.2)] rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-12 relative overflow-hidden">
        {/* Accent border top */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-[rgb(218,255,1)]" />
        
        {/* Glow effect */}
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-[rgba(218,255,1,0.05)] rounded-full blur-[100px] pointer-events-none" />
        
        <div className="relative z-10">
          <span
            className="text-[rgb(218,255,1)] text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] font-medium block mb-3 sm:mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            THE BLACK BOX PROBLEM
          </span>
          
          <h2
            className="text-[rgb(218,255,1)] text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
            data-testid="reality-check-title"
          >
            {realityCheck.title}
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
            {/* Left: Body Text */}
            <div>
              <div className="text-[rgb(218,218,218)] text-sm sm:text-base leading-relaxed whitespace-pre-line" style={{ fontFamily: "'Inter', sans-serif" }}>
                {realityCheck.body}
              </div>
            </div>
            
            {/* Right: Bullets & CTA */}
            <div className="flex flex-col justify-between">
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {realityCheck.bullets.map((bullet, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 sm:p-4 bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-lg sm:rounded-xl">
                    <Check className="text-[rgb(218,255,1)] h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <span className="text-white text-sm sm:text-base font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>{bullet}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-[rgb(218,255,1)] text-xs sm:text-sm font-bold tracking-wide mb-4 sm:mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                {realityCheck.cta}
              </p>
              
              <a
                href="mailto:leaver@itzgreenmusic.com?subject=UNLOCK%20MY%20PUBLISHER%20SHARE"
                className="inline-flex items-center justify-center sm:justify-start gap-2 sm:gap-3 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-xs sm:text-sm font-semibold tracking-[0.1em] px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4),0_8px_25px_rgba(218,255,1,0.3)] transition-all duration-300 w-full sm:w-fit group"
                style={{ fontFamily: "'Inter', sans-serif" }}
                data-testid="unlock-royalties-cta"
              >
                UNLOCK MY ROYALTIES
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* ═══════════════ LIVE LEDGER (Wall Street Terminal) ═══════════════ */
const LiveLedgerSection = () => (
  <Section className="bg-[rgb(26,28,30)] py-16 sm:py-20 md:py-24 border-y border-[rgba(255,255,255,0.06)]">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 sm:mb-10 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Database size={16} className="text-[rgb(218,255,1)]" />
            <span
              className="text-[rgb(218,255,1)] text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] font-medium"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              ACTIVE ASSET LEDGER
            </span>
          </div>
          <h2
            className="text-white text-xl sm:text-2xl md:text-3xl font-bold"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
          >
            Recent Catalog Reconciliations
          </h2>
          <p
            className="text-[rgb(161,161,170)] text-xs sm:text-sm mt-2"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Updated Q2 2026
          </p>
        </div>
        
        <a
          href="/clearance"
          className="inline-flex items-center gap-2 text-[rgb(218,255,1)] text-xs font-semibold tracking-[0.1em] hover:underline"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          VIEW CLEARANCE PROTOCOL
          <ExternalLink size={12} />
        </a>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[rgba(255,255,255,0.1)]">
              <th className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em] font-medium py-3 pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                REGISTRATION DATE
              </th>
              <th className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em] font-medium py-3 pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                ASSET TITLE
              </th>
              <th className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em] font-medium py-3 pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                ASSET TYPE
              </th>
              <th className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em] font-medium py-3 pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                STATUS
              </th>
              <th className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em] font-medium py-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                BMI WORK NO.
              </th>
            </tr>
          </thead>
          <tbody>
            {liveLedgerData.map((item, i) => (
              <tr key={i} className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[rgba(218,255,1,0.02)] transition-colors">
                <td className="text-[rgb(161,161,170)] text-xs font-mono py-4 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.date}
                </td>
                <td className="text-white text-sm font-semibold py-4 pr-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.title}
                </td>
                <td className="text-[rgb(161,161,170)] text-xs py-4 pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {item.type}
                </td>
                <td className="py-4 pr-4">
                  <span className="inline-flex items-center gap-1.5 text-[rgb(74,222,128)] text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-[rgb(74,222,128)]" />
                    {item.status}
                  </span>
                </td>
                <td className="text-[rgb(218,255,1)] text-xs font-mono py-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.bmiWorkNo}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {liveLedgerData.map((item, i) => (
          <div key={i} className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.08)] rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-white text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {item.title}
              </h4>
              <span className="inline-flex items-center gap-1 text-[rgb(74,222,128)] text-[10px] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-[rgb(74,222,128)]" />
                {item.status}
              </span>
            </div>
            <p className="text-[rgb(161,161,170)] text-xs mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
              {item.type}
            </p>
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-[rgb(161,161,170)] font-mono">{item.date}</span>
              <span className="text-[rgb(218,255,1)] font-mono">BMI: {item.bmiWorkNo}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ═══════════════ CAPABILITIES (INFRASTRUCTURE) ═══════════════ */
const CapabilitiesSection = () => (
  <Section id="services" className="bg-[rgb(26,28,30)] py-24 md:py-32">
    <div className="max-w-[1200px] mx-auto px-6 md:px-8">
      <span
        className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-4"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        CORE SERVICES
      </span>
      <h2
        className="text-white text-3xl md:text-4xl font-bold mb-6 max-w-lg"
        style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
      >
        ONE-STOP ASSET MANAGEMENT
      </h2>
      <p
        className="text-[rgb(161,161,170)] text-base leading-relaxed mb-16 max-w-2xl"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        We offer a dual administration service that simplifies licensing for television networks and maximizes revenue for our roster.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {capabilities.map((cap, i) => {
          const Icon = [Globe, Music, Zap][i] || Shield; // Simplified mapping
          return (
            <div
              key={cap.title}
              className="group bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 hover:border-[rgb(218,255,1)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] transition-all duration-300 relative overflow-hidden"
            >
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="w-11 h-11 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center mb-5">
                <Icon size={20} className="text-[rgb(218,255,1)]" />
              </div>
              <h3
                className="text-white text-sm font-semibold tracking-[0.1em] mb-3"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {cap.title}
              </h3>
              <p
                className="text-[rgb(161,161,170)] text-sm leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {cap.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </Section>
);

/* ═══════════════ DEAL STRUCTURE (NEW) ═══════════════ */
const DealStructureSection = () => (
  <Section className="bg-[rgb(17,17,19)] py-24 md:py-32">
    <div className="max-w-[1200px] mx-auto px-6 md:px-8">
      <span
        className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-4"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        THE DEAL STRUCTURE
      </span>
      <h2
        className="text-white text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6"
        style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
      >
        EXCLUSIVE CO-PUBLISHING & MASTER REPRESENTATION
      </h2>
      <p
        className="text-[rgb(161,161,170)] text-sm sm:text-base leading-relaxed mb-10 sm:mb-16 max-w-2xl"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        We operate strictly as an administrator, not a rights owner. Our standard representation agreement includes:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-16">
        {dealStructure.map((item, i) => {
            const icons = [Scale, Zap, Percent];
            const Icon = icons[i];
            return (
                <div key={item.label} className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl p-5 sm:p-8 flex flex-col items-center text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[rgba(218,255,1,0.1)] flex items-center justify-center mb-4 sm:mb-6 text-[rgb(218,255,1)]">
                        <Icon size={20} />
                    </div>
                    <h3 className="text-white text-base sm:text-lg font-bold mb-1 sm:mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {item.value}
                    </h3>
                    <p className="text-[rgb(161,161,170)] text-[10px] sm:text-xs tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {item.label}
                    </p>
                </div>
            )
        })}
      </div>

      {/* Contractual Integrity Sub-section */}
      <div className="bg-[rgba(218,255,1,0.03)] border border-[rgba(218,255,1,0.1)] rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-12 text-center max-w-3xl mx-auto">
        <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            CONTRACTUAL INTEGRITY
        </h3>
        <p className="text-[rgb(161,161,170)] text-xs sm:text-sm md:text-base leading-relaxed mb-6 sm:mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            We believe in artist autonomy. Our Exclusive Co-Publishing & Master Representation agreement is designed to protect the creator, not the middleman.
        </p>
        
        {/* Dual-Button Strategy */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          {/* Primary - SignNow */}
          <a
              href={signNowConfig.signingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-xs font-semibold tracking-[0.08em] sm:tracking-[0.1em] px-5 sm:px-6 py-3 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_25px_rgba(218,255,1,0.4)] transition-all duration-300 group w-full sm:w-auto"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="execute-agreement-deal-section"
          >
              <PenTool size={14} />
              <span className="whitespace-nowrap">{signNowConfig.primaryButtonText}</span>
          </a>
          
          {/* Secondary - PDF Preview */}
          <a
              href={signNowConfig.pdfPreviewUrl}
              download="ItzGreen_Sample_Agreement.pdf"
              className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs font-medium tracking-[0.1em] hover:text-[rgb(218,255,1)] transition-colors"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="preview-terms-deal-section"
          >
              <FileText size={14} />
              {signNowConfig.secondaryButtonText}
          </a>
        </div>
      </div>
    </div>
  </Section>
);

/* ═══════════════ METADATA AUDIT DIALOG ═══════════════ */
const MetadataAuditDialog = ({ open, onOpenChange }) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[rgb(26,28,30)] border-[rgb(63,63,63)] text-white sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>METADATA SELF-AUDIT</DialogTitle>
                    <DialogDescription className="text-[rgb(161,161,170)]">
                        Before submitting, verify your stems meet our broadcast standards.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                    {[
                        "24-bit / 48kHz WAV Format",
                        "No Limiter on Master Bus (-6dB Headroom)",
                        "Key & BPM Labelled in Filename",
                        "Legal Name & Contact in Comments Metadata",
                        "All Samples Cleared / Royalty-Free"
                    ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-[rgb(17,17,19)] rounded-lg border border-[rgba(255,255,255,0.05)]">
                            <Check className="text-[rgb(218,255,1)] h-4 w-4" />
                            <span className="text-sm text-[rgb(218,218,218)]">{item}</span>
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button 
                        onClick={() => onOpenChange(false)}
                        className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-bold tracking-wider"
                    >
                        I CONFIRM
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/* ═══════════════ ARTIST ADMINISTRATION SUITE (NEW) ═══════════════ */
const ArtistAdministrationSection = () => {
  const [auditOpen, setAuditOpen] = useState(false);

  return (
    <Section id="resources" className="bg-[rgb(17,17,19)] py-24 md:py-32">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <span
          className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          FOR PRODUCERS
        </span>
        <h2
          className="text-white text-3xl md:text-4xl font-bold mb-4"
          style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
        >
          ARTIST ADMINISTRATION SUITE
        </h2>
        <p
          className="text-[rgb(161,161,170)] text-base mb-16 max-w-lg"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Professional tools to manage your assets, validate metadata, and secure your rights.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {artistResources && artistResources.map((resource, i) => {
              const icons = {
                  "CheckSquare": CheckSquare,
                  "FilePlus": FilePlus,
                  "Users": Users,
                  "BookOpen": BookOpen
              };
              const Icon = icons[resource.icon] || FileText;
              
              return (
                  <div key={resource.id} className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl p-6 hover:border-[rgb(218,255,1)] transition-colors duration-300 group">
                      <div className="w-10 h-10 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center mb-4 text-[rgb(218,255,1)]">
                          <Icon size={20} />
                      </div>
                      <h3 className="text-white text-sm font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                          {resource.title}
                      </h3>
                      <p className="text-[rgb(161,161,170)] text-xs leading-relaxed mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {resource.description}
                      </p>
                      
                      {resource.id === 'one-stop' ? (
                           <div className="flex items-center gap-2 mt-auto">
                              <Badge variant="outline" className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] text-[10px] tracking-wider">
                                  <Award size={10} className="mr-1" /> CERTIFIED
                              </Badge>
                           </div>
                      ) : resource.isRoute ? (
                          <Link
                              to={resource.link}
                              className="text-[rgb(218,255,1)] text-[10px] font-bold tracking-[0.15em] flex items-center gap-1 group-hover:gap-2 transition-all mt-auto cursor-pointer"
                              style={{ fontFamily: "'Inter', sans-serif" }}
                          >
                              {resource.action} <ArrowRight size={12} />
                          </Link>
                      ) : (
                          <a
                              href={resource.link}
                              target={resource.link !== "#" && !resource.link.startsWith("mailto") ? "_blank" : undefined}
                              className="text-[rgb(218,255,1)] text-[10px] font-bold tracking-[0.15em] flex items-center gap-1 group-hover:gap-2 transition-all mt-auto cursor-pointer"
                              style={{ fontFamily: "'Inter', sans-serif" }}
                          >
                              {resource.action} <ArrowRight size={12} />
                          </a>
                      )}
                  </div>
              );
          })}
        </div>
      </div>
      <MetadataAuditDialog open={auditOpen} onOpenChange={setAuditOpen} />
    </Section>
  );
};

/* ═══════════════ SERVICE BLOCK (BRIEFS) ═══════════════ */
const ServiceBlock = ({ service, reversed }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  
  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[auto] lg:min-h-[500px] ${
        reversed ? "lg:[direction:rtl]" : ""
      }`}
    >
      {/* Image - Visible on all screen sizes */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? 40 : -40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden lg:[direction:ltr] h-[250px] sm:h-[300px] lg:h-auto"
      >
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(17,17,19)] via-transparent to-transparent opacity-60" />
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? -40 : 40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="flex flex-col justify-center px-5 sm:px-8 md:px-16 py-10 sm:py-12 lg:py-16 bg-[rgb(17,17,19)] lg:[direction:ltr]"
      >
        <span
          className="text-[rgb(218,255,1)] text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] font-medium mb-3 sm:mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {service.category}
        </span>

        <h2
          className="text-white text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 leading-[1.05]"
          style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
        >
          {service.title}
        </h2>

        <p
          className="text-[rgb(218,218,218)] text-sm sm:text-base leading-relaxed mb-6 sm:mb-8 max-w-md"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {service.description}
        </p>

        {/* Brief Stats */}
        <div className="flex gap-6 sm:gap-8 mb-8 sm:mb-10">
          {service.stats.map((stat) => (
            <div key={stat.label}>
              <p
                className="text-[rgb(218,255,1)] text-lg sm:text-xl font-bold"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {stat.value}
              </p>
              <p
                className="text-[rgb(161,161,170)] text-[9px] sm:text-[10px] tracking-[0.15em] mt-1"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {stat.label.toUpperCase()}
              </p>
            </div>
          ))}
        </div>

        <a
          href={service.buttonLink}
          className="inline-flex items-center justify-center sm:justify-start gap-2 sm:gap-3 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-xs sm:text-sm font-semibold tracking-[0.1em] px-6 sm:px-7 py-3 sm:py-3.5 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4),0_8px_25px_rgba(218,255,1,0.3)] transition-all duration-300 w-full sm:w-fit group"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {service.buttonText}
          <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </a>
      </motion.div>
    </div>
  );
};

/* ═══════════════ PLACEMENT PARTNERS ═══════════════ */
const PlacementStrip = () => (
  <div className="bg-[rgb(26,28,30)] border-y border-[rgba(255,255,255,0.06)] py-10">
    <div className="max-w-[1200px] mx-auto px-6 md:px-8">
      <p
        className="text-[rgb(161,161,170)] text-[10px] tracking-[0.35em] text-center mb-6"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        ACTIVE & SUBMITTED PLACEMENTS
      </p>
      <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
        {placementPartners.map((partner) => (
          <div key={partner.name} className="group flex items-center gap-2">
            <span
              className="text-white/40 group-hover:text-[rgb(218,255,1)] text-xl md:text-2xl font-bold tracking-[0.1em] transition-colors duration-300"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {partner.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ═══════════════ VAULT TEASER ═══════════════ */
const VaultTeaserSection = () => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
      className="bg-[rgb(17,17,19)] py-20 md:py-28"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="relative bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-10 md:p-16 overflow-hidden">
          {/* Glow accent */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-[rgb(218,255,1)]" />
          <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-[rgba(218,255,1,0.04)] rounded-full blur-[100px] pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Lock size={18} className="text-[rgb(218,255,1)]" />
                <span
                  className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {vaultTeaser.subline.toUpperCase()}
                </span>
              </div>
              <h2
                className="text-white text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
              >
                {vaultTeaser.headline}
              </h2>
              <p
                className="text-[rgb(161,161,170)] text-base leading-relaxed max-w-lg"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {vaultTeaser.description}
              </p>
            </div>

            <Link
              to={vaultTeaser.ctaLink}
              className="inline-flex items-center gap-3 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-semibold tracking-[0.1em] px-8 py-4 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)] transition-all duration-300 group whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {vaultTeaser.ctaText}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

/* ═══════════════ REDACTED CREDITS ═══════════════ */
const RedactedCreditsSection = () => (
  <Section className="bg-[rgb(17,17,19)] py-24 md:py-32">
    <div className="max-w-[1200px] mx-auto px-6 md:px-8">
      <span
        className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-4"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        RECENT OPERATIONS
      </span>
      <h2
        className="text-white text-3xl md:text-4xl font-bold mb-16 max-w-lg"
        style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
      >
        CLASSIFIED PLACEMENT LOG
      </h2>

      <div className="space-y-3">
        {redactedCredits.map((credit, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] rounded-xl p-5 flex flex-wrap items-center gap-x-8 gap-y-3"
          >
            <div className="flex items-center gap-3 min-w-[200px]">
              {credit.project === "[REDACTED]" ? (
                <EyeOff size={14} className="text-[rgb(161,161,170)]" />
              ) : (
                <Eye size={14} className="text-[rgb(218,255,1)]" />
              )}
              <span
                className={`text-sm font-semibold tracking-wide ${
                  credit.project === "[REDACTED]"
                    ? "bg-[rgb(63,63,63)] text-transparent select-none px-16 py-0.5 rounded"
                    : "text-white"
                }`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {credit.project === "[REDACTED]" ? "REDACTED" : credit.project}
              </span>
            </div>

            {credit.network && (
              <div className="flex items-center gap-2">
                <span className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  NETWORK:
                </span>
                <span className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {credit.network}
                </span>
              </div>
            )}

            {credit.studio && (
              <div className="flex items-center gap-2">
                <span className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  STUDIO:
                </span>
                <span className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {credit.studio}
                </span>
              </div>
            )}

            <div className="ml-auto">
              <Badge
                variant="outline"
                className={`text-[10px] tracking-[0.15em] rounded-md bg-transparent ${
                  credit.status === "CLEARED"
                    ? "border-[rgb(218,255,1)] text-[rgb(218,255,1)]"
                    : credit.status === "SUBMITTED"
                    ? "border-amber-400 text-amber-400"
                    : "border-[rgb(63,63,63)] text-[rgb(161,161,170)]"
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {credit.status}
              </Badge>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </Section>
);

/* ═══════════════ EDITOR'S TOOLKIT ═══════════════ */
const EditorToolkitSection = () => (
  <Section className="bg-[rgb(26,28,30)] py-24 md:py-32">
    <div className="max-w-[1200px] mx-auto px-6 md:px-8">
      <span
        className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-4"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        FOR EDITORS
      </span>
      <h2
        className="text-white text-3xl md:text-4xl font-bold mb-4"
        style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
      >
        POST-PRODUCTION ASSETS
      </h2>
      <p
        className="text-[rgb(161,161,170)] text-base mb-16 max-w-lg"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        Ready-to-cut sound design elements for your edit bay.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {postProductionAssets.map((pack) => (
          <div
            key={pack.title}
            className="group bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-7 hover:border-[rgb(218,255,1)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] transition-all duration-300 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center">
                <Package size={20} className="text-[rgb(218,255,1)]" />
              </div>
              <span
                className="text-[rgb(161,161,170)] text-xs"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {pack.format}
              </span>
            </div>

            <h3
              className="text-white text-sm font-semibold tracking-[0.1em] mb-2"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {pack.title}
            </h3>
            <p
              className="text-[rgb(161,161,170)] text-sm leading-relaxed mb-5"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {pack.description}
            </p>

            <a
              href={pack.downloadUrl}
              target={pack.downloadUrl !== "#" ? "_blank" : undefined}
              rel={pack.downloadUrl !== "#" ? "noopener noreferrer" : undefined}
              className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.1em] transition-all duration-200 ${
                pack.status === "active"
                  ? "text-[rgb(218,255,1)] hover:underline"
                  : "text-[rgb(161,161,170)] cursor-default"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
              onClick={(e) => pack.status !== "active" && e.preventDefault()}
            >
              <FileDown size={14} />
              {pack.status === "active" ? "DOWNLOAD PACK" : "COMING SOON"}
            </a>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ═══════════════ SUPERVISOR TOOLKIT ═══════════════ */
const SupervisorToolkitSection = () => (
  <Section className="bg-[rgb(17,17,19)] py-24 md:py-32">
    <div className="max-w-[1200px] mx-auto px-6 md:px-8">
      <span
        className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-4"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        FOR MUSIC SUPERVISORS
      </span>
      <h2
        className="text-white text-3xl md:text-4xl font-bold mb-4"
        style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
      >
        SUPERVISOR TOOLKIT
      </h2>
      <p
        className="text-[rgb(161,161,170)] text-base mb-16 max-w-lg"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        Everything you need to clear, cut, and cue — in one place.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {supervisorToolkit.map((res) => (
          <div
            key={res.title}
            className="group bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-7 hover:border-[rgb(218,255,1)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] transition-all duration-300 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center">
                <FileText size={20} className="text-[rgb(218,255,1)]" />
              </div>
              <Badge
                variant="outline"
                className="border-[rgb(63,63,63)] text-[rgb(161,161,170)] bg-transparent text-[10px] tracking-[0.15em] rounded-md"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {res.format}
              </Badge>
            </div>

            <h3
              className="text-white text-sm font-semibold tracking-[0.1em] mb-2"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {res.title}
            </h3>
            <p
              className="text-[rgb(161,161,170)] text-sm leading-relaxed mb-5"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {res.description}
            </p>

            <a
              href={res.downloadUrl}
              target={res.downloadUrl !== "#" ? "_blank" : undefined}
              rel={res.downloadUrl !== "#" ? "noopener noreferrer" : undefined}
              className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.1em] transition-all duration-200 ${
                res.status === "active"
                  ? "text-[rgb(218,255,1)] hover:underline"
                  : "text-[rgb(161,161,170)] cursor-default"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
              onClick={(e) => res.status !== "active" && e.preventDefault()}
            >
              <Download size={14} />
              {res.status === "active" ? "DOWNLOAD" : "COMING SOON"}
            </a>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ═══════════════ CONTACT ═══════════════ */
const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", project: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const isConfigured =
    emailjsConfig.serviceId !== "YOUR_SERVICE_ID" &&
    emailjsConfig.templateId !== "YOUR_TEMPLATE_ID" &&
    emailjsConfig.publicKey !== "YOUR_PUBLIC_KEY";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setError("");
    setSending(true);

    if (isConfigured) {
      try {
        await emailjs.send(
          emailjsConfig.serviceId,
          emailjsConfig.templateId,
          {
            from_name: form.name,
            from_email: form.email,
            project: form.project || "Not specified",
            message: form.message,
          },
          emailjsConfig.publicKey
        );
        setSubmitted(true);
      } catch (err) {
        setError("Failed to send. Please email us directly.");
      }
    } else {
      // Demo mode — simulate send
      await new Promise((r) => setTimeout(r, 800));
      setSubmitted(true);
    }

    setSending(false);
    if (!error) {
      setTimeout(() => {
        setSubmitted(false);
        setForm({ name: "", email: "", project: "", message: "" });
      }, 4000);
    }
  };

  return (
    <Section id="contact" className="bg-[rgb(17,17,19)] py-24 md:py-32">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left Info */}
          <div>
            <span
              className="text-[rgb(218,255,1)] text-[10px] tracking-[0.35em] font-medium block mb-4"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              OUR INFRASTRUCTURE. YOUR ART.
            </span>
            <h2
              className="text-white text-3xl md:text-4xl font-bold mb-6"
              style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
            >
              LET'S PLACE SOME MUSIC.
            </h2>
            <p
              className="text-[rgb(161,161,170)] text-base leading-relaxed mb-6 max-w-md"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Managed by Leaver Hall, ItzGreenMusic is an exclusive publishing administration and A&R firm. We bridge the gap between elite human producers and the legal requirements of major television networks.
            </p>
            <p
              className="text-[rgb(161,161,170)] text-base leading-relaxed mb-6 max-w-md"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              We operate on a strict 200% One-Stop, Zero Generative-AI model. We handle BMI/The MLC registration, metadata architecture, and direct executive pitching. You maintain 100% of your Writer's Share and complete creative control. We secure the placement; we split the sync fee.
            </p>

            <div className="mb-10 p-6 bg-[rgb(26,28,30)] border border-[rgb(63,63,63)] rounded-xl">
               <p className="text-white font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>{contactInfo.manager}</p>
               <p className="text-[rgb(161,161,170)] text-xs mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>{contactInfo.specialty}</p>
               <div className="space-y-2">
                 <p className="text-[rgb(218,255,1)] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{contactInfo.email}</p>
                 <p className="text-[rgb(161,161,170)] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{contactInfo.location}</p>
               </div>
            </div>

          </div>

          {/* Right Form */}
          <div>
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center h-full text-center"
              >
                <CheckCircle size={48} className="text-[rgb(218,255,1)] mb-4" />
                <h3
                  className="text-white text-2xl font-bold mb-2"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  INQUIRY SENT
                </h3>
                <p className="text-[rgb(161,161,170)] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                  We'll be in touch within 48 hours.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      className="text-[rgb(161,161,170)] text-xs tracking-[0.15em] mb-2 block"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      NAME
                    </label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="bg-[rgb(26,28,30)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(100,100,110)] rounded-xl h-12 focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      required
                    />
                  </div>
                  <div>
                    <label
                      className="text-[rgb(161,161,170)] text-xs tracking-[0.15em] mb-2 block"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      EMAIL
                    </label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email@company.com"
                      className="bg-[rgb(26,28,30)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(100,100,110)] rounded-xl h-12 focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="text-[rgb(161,161,170)] text-xs tracking-[0.15em] mb-2 block"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    PROJECT
                  </label>
                  <Input
                    value={form.project}
                    onChange={(e) => setForm({ ...form, project: e.target.value })}
                    placeholder="Film, TV Series, Streaming..."
                    className="bg-[rgb(26,28,30)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(100,100,110)] rounded-xl h-12 focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                <div>
                  <label
                    className="text-[rgb(161,161,170)] text-xs tracking-[0.15em] mb-2 block"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    MESSAGE
                  </label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your project, timeline, and licensing needs..."
                    rows={5}
                    className="bg-[rgb(26,28,30)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(100,100,110)] rounded-xl resize-none focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold text-sm tracking-[0.1em] px-8 py-3 h-auto rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4),0_8px_25px_rgba(218,255,1,0.3)] transition-all duration-300 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {sending ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Send size={16} className="mr-2" />
                  )}
                  {sending ? "SENDING..." : "SUBMIT INQUIRY"}
                </Button>

                {error && (
                  <p className="text-red-400 text-sm mt-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {error}
                  </p>
                )}

                {!isConfigured && (
                  <p className="text-[rgb(161,161,170)] text-xs mt-3 italic" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Demo mode — configure EmailJS in mock.js to enable live delivery.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ═══════════════ REQUIRED DELIVERABLES (NEW) ═══════════════ */
const DeliverablesSection = () => (
    <div className="bg-[rgb(26,28,30)] py-16 border-y border-[rgba(255,255,255,0.06)]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
            <h3 className="text-white text-xl font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                REQUIRED DELIVERABLES FOR INGESTION
            </h3>
            <p className="text-[rgb(161,161,170)] text-sm leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                Do not submit MP3s. To be considered for roster placement, artists must be prepared to deliver high-resolution WAV files, instrumental versions, clean versions, tracked-out stems, and complete metadata.
            </p>
            
            {/* Sample Warning */}
            <div className="bg-[rgba(239,68,68,0.1)] border border-red-500/30 rounded-lg px-4 py-3 mb-6 inline-block">
              <p className="text-red-400 text-xs font-bold tracking-[0.15em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                ⚠️ NO UNCLEARED SAMPLES. 100% ORIGINAL COMPOSITIONS ONLY.
              </p>
            </div>
            
            {/* Dual-Button Strategy */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {/* Primary - SignNow */}
              <a
                  href={signNowConfig.signingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-xs font-semibold tracking-widest px-5 py-2.5 rounded-lg hover:bg-[rgb(166,190,21)] transition-all duration-300"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  data-testid="execute-agreement-deliverables"
              >
                  <PenTool size={12} />
                  SIGN DIGITALLY
              </a>
              
              {/* Secondary - PDF Preview */}
              <a
                  href={signNowConfig.pdfPreviewUrl}
                  download="ItzGreen_Sample_Agreement.pdf"
                  className="inline-flex items-center gap-2 text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] transition-colors text-xs font-semibold tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  data-testid="preview-terms-deliverables"
              >
                  <FileText size={12} />
                  PREVIEW TERMS
              </a>
            </div>
        </div>
    </div>
);

/* ═══════════════ A&R NEWS / CORPORATE UPDATES ═══════════════ */
const arNewsData = [
  {
    badge: "UPDATE 01: Q2 PAYLOAD SECURED",
    title: "BROADCAST STEMS & ALTERNATE MIXES",
    content: "The ItzGreen Music A&R pipeline has successfully cleared and vaulted the final Q2 deliverables for the Nijay Scott catalog. Full broadcast packages (Main, Instrumental, Acapella, TV Mix) for \"Island Love\" and \"Used\" are now live. All assets provide immediate, frictionless one-stop administration for post-production editing bays.",
    icon: TrendingUp,
    date: "Q2 2026"
  },
  {
    badge: "UPDATE 02: STREAMER-SAFE INFRASTRUCTURE",
    title: "ZERO-LIABILITY FOR GAMING & E-SPORTS",
    content: "In response to the growing DMCA liability crisis in competitive gaming, ItzGreen Music has expanded its B2B catalog to service high-tension and urban e-sports slates. We govern our Master and Publishing internally, guaranteeing that all gaming broadcast assets are 100% human-produced, sample-free, and entirely streamer-safe.",
    icon: Shield,
    date: "Q2 2026"
  },
  {
    badge: "UPDATE 03: ACTIVE PROCUREMENT PIPELINE",
    title: "CINEMATIC TENSION & NEO-CLASSICAL CUES",
    content: "The ItzGreen A&R pipeline is currently locked on premium television slates. Internal ingestion is actively sourcing and clearing high-tension dramatic cues, authentic street-level hip-hop, and minimalist neo-classical piano. All incoming assets are strictly routed through our 1-Page administration agreement prior to Vault ingestion.",
    icon: Database,
    date: "Q2 2026"
  }
];

const ARNewsSection = () => (
  <Section id="news" className="bg-[rgb(17,17,19)] py-16 sm:py-24 md:py-32">
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 md:px-8">
      <span
        className="text-[rgb(218,255,1)] text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] font-medium block mb-3 sm:mb-4 text-center"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        TERMINAL UPDATES
      </span>
      <h2
        className="text-white text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-center"
        style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
      >
        EXECUTIVE <span className="text-[rgb(218,255,1)]">BRIEFS</span>
      </h2>
      <p
        className="text-[rgb(161,161,170)] text-sm text-center max-w-xl mx-auto mb-10"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        Active intelligence from our Rights Administration and A&R divisions.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {arNewsData.map((news, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.15 }}
            className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 hover:border-[rgba(218,255,1,0.3)] transition-all duration-300"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[rgba(218,255,1,0.1)] flex items-center justify-center">
                  <news.icon size={18} className="text-[rgb(218,255,1)]" />
                </div>
                <div>
                  <span
                    className="text-[rgb(218,255,1)] text-[9px] tracking-[0.2em] font-semibold"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {news.badge}
                  </span>
                  <p
                    className="text-[rgb(100,100,110)] text-[10px]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {news.date}
                  </p>
                </div>
              </div>
              <Newspaper size={14} className="text-[rgb(63,63,63)]" />
            </div>
            
            {/* Title */}
            <h3
              className="text-white text-sm sm:text-base font-bold mb-3"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {news.title}
            </h3>
            
            {/* Content */}
            <p
              className="text-[rgb(161,161,170)] text-xs leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {news.content}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </Section>
);

/* ═══════════════ FAQ SECTION (OBJECTION HANDLING) ═══════════════ */
const faqData = [
  {
    question: "Can I still sell this beat on BeatStars?",
    answer: "Yes. We only take exclusive rights for Sync Licensing (TV/Film). You can still lease the beat to rappers online. We don't touch your lease money."
  },
  {
    question: "Do you take ownership of my publishing?",
    answer: "No. You keep 100% of your Writer's Share. We simply administer the Publisher's Share to collect royalties you can't reach."
  },
  {
    question: "What if the beat uses a sample?",
    answer: "Automatic Rejection. We only accept 100% original compositions or fully cleared royalty-free loops. No uncleared samples."
  }
];

const FAQSection = () => (
  <Section id="faq" className="bg-[rgb(17,17,19)] py-16 sm:py-24 md:py-32">
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-8">
      <span
        className="text-[rgb(218,255,1)] text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] font-medium block mb-3 sm:mb-4 text-center"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        OBJECTION HANDLING
      </span>
      <h2
        className="text-white text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 text-center"
        style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.01em" }}
      >
        FREQUENTLY ASKED QUESTIONS
      </h2>
      
      <div className="space-y-3 sm:space-y-4">
        {faqData.map((faq, i) => (
          <div 
            key={i}
            className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-lg sm:rounded-xl p-4 sm:p-6 hover:border-[rgba(218,255,1,0.3)] transition-colors"
          >
            <h3 
              className="text-[rgb(218,255,1)] text-sm sm:text-base font-bold mb-2 sm:mb-3 flex items-start gap-2 sm:gap-3"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              <span className="text-[rgb(218,255,1)] opacity-50">Q:</span>
              {faq.question}
            </h3>
            <p 
              className="text-[rgb(218,218,218)] text-xs sm:text-sm leading-relaxed pl-5 sm:pl-7"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="text-[rgb(161,161,170)] font-bold mr-2">A:</span>
              {faq.answer}
            </p>
          </div>
        ))}
      </div>
      
      {/* Additional CTA */}
      <div className="text-center mt-8 sm:mt-12">
        <p 
          className="text-[rgb(161,161,170)] text-xs sm:text-sm mb-3 sm:mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Still have questions?
        </p>
        <a
          href="mailto:leaver@itzgreenmusic.com?subject=Question%20About%20ItzGreen%20Music"
          className="inline-flex items-center gap-2 text-[rgb(218,255,1)] text-xs sm:text-sm font-semibold tracking-[0.1em] hover:underline"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <Mail size={14} className="sm:hidden" />
          <Mail size={16} className="hidden sm:block" />
          ASK DIRECTLY
        </a>
      </div>
    </div>
  </Section>
);

/* ═══════════════ HOME PAGE ═══════════════ */
const HomePage = () => (
  <main className="pb-10">
    <LegalBanner />
    <HeroSection />
    <RealityCheckSection />
    <LiveLedgerSection />
    <CapabilitiesSection />
    <DealStructureSection />
    <ArtistAdministrationSection />
    
    <div id="catalog">
      {serviceBlocks.map((service, i) => (
        <ServiceBlock key={service.id} service={service} reversed={i % 2 !== 0} />
      ))}
    </div>
    
    <DeliverablesSection />
    <ARNewsSection />
    <FAQSection />
    <PlacementStrip />
  </main>
);

export default HomePage;
